/**
 * Probe 하나를 종료 상태까지 따라가는 폴링 컨트롤러.
 *
 * 생성과 활성화 시점이 다르고 타이머 · 진행 중 요청을 소유하므로 `start`/`stop`/`dispose` 를
 * 두고 반복 호출에 안전하게 만든다(규칙 §8). 세대 번호로 **늦게 온 응답을 버린다** — 대상을
 * 바꾸거나 화면을 떠난 뒤 도착한 응답이 새 상태를 덮으면 안 된다(규칙 §6).
 *
 * 문서가 숨겨지면 멈추고 돌아오면 즉시 한 번 조회한다 — 배경 탭이 250ms 왕복을 계속 쓸 이유가 없다.
 *
 * Owns:
 * - 폴링 타이머, 세대 무효화, 문서 가시성 반응, 정리.
 *
 * Does not own:
 * - 간격 정책 — `probePolling.ts`. 조회 — 주입받는 `fetchProbe`. 화면 상태 — 페이지.
 */
import { isPageHidden } from "@/shared/utils/pageVisibility";
import type { Probe } from "../domain/probe";
import { probePollIntervalMs } from "./probePolling";

export type ProbePollerOptions = {
  fetchProbe: (probeUuid: string) => Promise<Probe>;
  onProbe: (probe: Probe) => void;
  onError: (error: unknown) => void;
  /** 테스트가 시간을 고정할 수 있게 주입한다. */
  now?: () => number;
};

export class ProbePoller {
  private readonly options: ProbePollerOptions;
  private readonly now: () => number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private generation = 0;
  private probeUuid: string | null = null;
  private startedAtMs = 0;
  private disposed = false;
  private visibilityListener: (() => void) | null = null;

  constructor(options: ProbePollerOptions) {
    this.options = options;
    this.now = options.now ?? (() => Date.now());
  }

  /** 대상 Probe 를 따라가기 시작한다. 같은 uuid 로 다시 불러도 조회가 겹치지 않는다. */
  start(probeUuid: string): void {
    if (this.disposed || this.probeUuid === probeUuid) {
      return;
    }

    this.probeUuid = probeUuid;
    this.startedAtMs = this.now();
    this.generation += 1;
    this.clearTimer();
    this.attachVisibility();
    void this.poll(this.generation);
  }

  /** 따라가기를 멈춘다. 진행 중 응답은 세대가 어긋나 버려진다. */
  stop(): void {
    this.probeUuid = null;
    this.generation += 1;
    this.clearTimer();
    this.detachVisibility();
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
  }

  private async poll(generation: number): Promise<void> {
    const probeUuid = this.probeUuid;
    if (probeUuid === null || generation !== this.generation) {
      return;
    }

    try {
      const probe = await this.options.fetchProbe(probeUuid);
      if (generation !== this.generation) {
        return;
      }

      this.options.onProbe(probe);
      const interval = probePollIntervalMs(probe.state, this.now() - this.startedAtMs);
      if (interval === null) {
        this.stop();
        return;
      }
      this.schedule(generation, interval);
    } catch (error: unknown) {
      if (generation !== this.generation) {
        return;
      }
      // 일시적 실패로 관측을 끊지 않는다 — 다음 주기에 다시 묻는다. 401/403 은 apiClient 가
      // 세션을 무효화하고, 그 경로에서 페이지가 언마운트되며 dispose 가 폴링을 끝낸다.
      this.options.onError(error);
      this.schedule(generation, probePollIntervalMs("collecting", this.now() - this.startedAtMs));
    }
  }

  private schedule(generation: number, intervalMs: number | null): void {
    if (intervalMs === null || generation !== this.generation) {
      return;
    }

    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      if (isPageHidden()) {
        return;
      }
      void this.poll(generation);
    }, intervalMs);
  }

  private attachVisibility(): void {
    if (this.visibilityListener !== null || typeof document === "undefined") {
      return;
    }

    const listener = () => {
      if (isPageHidden() || this.probeUuid === null) {
        return;
      }
      // 돌아오면 기다리지 않고 지금 상태를 묻는다.
      this.clearTimer();
      void this.poll(this.generation);
    };
    this.visibilityListener = listener;
    document.addEventListener("visibilitychange", listener);
  }

  private detachVisibility(): void {
    if (this.visibilityListener === null || typeof document === "undefined") {
      return;
    }
    document.removeEventListener("visibilitychange", this.visibilityListener);
    this.visibilityListener = null;
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
